import { motion, useReducedMotion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCategoryGradient, gradientCss } from '../lib/categoryGradient';
import { incrementClicks } from '../services/siteContentService';
import '../rooQrCard.css';
import '../velocityRebelCard.css';

const { FiArrowUpRight, FiHeadphones, FiImage, FiMusic, FiPackage, FiTag } = FiIcons;

const categoryIcons = {
  audio: FiHeadphones,
  music: FiMusic,
  shop: FiPackage,
  deal: FiTag
};

function getHighlightType(item) {
  const title = String(item.title || '').trim().toLowerCase();
  const url = String(item.url || '').trim().toLowerCase();
  if (title === 'rooqr' || url.includes('rooqr.com')) return 'rooqr';
  if (title === 'velocity rebel' || url.includes('velocityrebel.netlify.app')) return 'velocity';
  return '';
}

export default function ContentCard({ item, entity = 'links', featured = false, index = 0 }) {
  const reduceMotion = useReducedMotion();
  const isPortfolio = entity === 'portfolio';
  const highlightType = isPortfolio ? getHighlightType(item) : '';
  const isHighlighted = Boolean(highlightType);
  const useFeaturedLayout = featured && !isPortfolio;
  const background = gradientCss(
    getCategoryGradient(item.title, `${item.category} ${item.subcategory}`)
  );
  const CategoryIcon = categoryIcons[item.category] || FiImage;
  const portfolioLabel = [
    item.category || 'Creative project',
    item.subcategory || item.type
  ].filter(Boolean).join(' · ');

  const trackClick = () => {
    if (!String(item.id).startsWith('fallback')) {
      incrementClicks(entity, item).catch(() => {});
    }
  };

  const classNames = [
    'content-card',
    useFeaturedLayout ? 'featured-card' : 'regular-card',
    isPortfolio ? 'portfolio-content-card' : '',
    isHighlighted ? 'rooqr-highlight-card' : '',
    highlightType === 'velocity' ? 'velocity-highlight-card' : ''
  ].filter(Boolean).join(' ');

  return (
    <motion.a
      href={item.url}
      target={item.url?.startsWith('#') ? undefined : '_blank'}
      rel="noreferrer"
      onClick={trackClick}
      className={classNames}
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.22,
        delay: reduceMotion ? 0 : Math.min(index * 0.025, 0.12),
        ease: 'easeOut'
      }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
    >
      {isPortfolio ? (
        <div className="portfolio-title-panel" style={{ background }}>
          {isHighlighted && (
            <span className="rooqr-featured-label" aria-label="Featured project">
              Featured
            </span>
          )}
          <span className="portfolio-category-label" title={portfolioLabel}>
            {portfolioLabel}
          </span>
          <strong>{item.title}</strong>
        </div>
      ) : (
        <div className="card-media" style={{ background }}>
          {item.image_url ? (
            <img
              src={item.image_url}
              alt=""
              loading={featured ? 'eager' : 'lazy'}
              decoding="async"
            />
          ) : (
            <SafeIcon icon={CategoryIcon} className="placeholder-icon" />
          )}
        </div>
      )}
      <div className="card-copy">
        {!isPortfolio && (
          <>
            <span className="card-category">{item.category || entity}</span>
            <h3>{item.title}</h3>
          </>
        )}
        {item.description && <p>{item.description}</p>}
        {isPortfolio && <span className="portfolio-visit">Explore project</span>}
      </div>
      <SafeIcon icon={FiArrowUpRight} className="card-arrow" />
    </motion.a>
  );
}